import { useState } from 'react';
import apiService from '../services/api';

/**
 * Custom hook for managing bail summary functionality
 * @param {object} bail - The bail data
 * @param {function} onUpdate - Callback function to execute after successful updates
 * @returns {object} - Contains states and functions for the bail summary
 */
const useBailSummary = (bail, onUpdate) => {
  // Helper function to format dates to YYYY-MM-DD format
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    try {
      const dateParts = dateString.split('/');
      if (dateParts.length === 3) {
        return `${dateParts[2]}-${dateParts[1].padStart(2, '0')}-${dateParts[0].padStart(2, '0')}`;
      }
    } catch (err) {
      console.error("Error formatting date:", err);
    }
    return '';
  };

  const initialStartDate = formatDateForInput(bail?.periode?.debut);
  const initialEndDate = formatDateForInput(bail?.periode?.fin);
  
  const [showStartDateEdit, setShowStartDateEdit] = useState(false);
  const [showEndDateEdit, setShowEndDateEdit] = useState(false);
  const [showFactureClotureEdit, setShowFactureClotureEdit] = useState(false);
  const [newStartDate, setNewStartDate] = useState(initialStartDate);
  const [newEndDate, setNewEndDate] = useState(initialEndDate);
  const [newFactureCloture, setNewFactureCloture] = useState(bail?.montants?.factureCloture || 0);
  const [updating, setUpdating] = useState(false);

  // Toggle functions
  const toggleStartDateEdit = () => {
    // Set current start date when opening the form
    if (!showStartDateEdit) {
      // We've already initialized with the formatted date, but refresh it just in case
      const formattedDate = formatDateForInput(bail?.periode?.debut);
      if (formattedDate) {
        setNewStartDate(formattedDate);
      }
    }
    setShowStartDateEdit(!showStartDateEdit);
  };

  const toggleEndDateEdit = () => {
    // Set current end date when opening the form
    if (!showEndDateEdit) {
      // We've already initialized with the formatted date, but refresh it just in case
      const formattedDate = formatDateForInput(bail?.periode?.fin);
      if (formattedDate) {
        setNewEndDate(formattedDate);
      }
    }
    setShowEndDateEdit(!showEndDateEdit);
  };

  const toggleFactureClotureEdit = () => {
    setShowFactureClotureEdit(!showFactureClotureEdit);
    setNewFactureCloture(bail?.montants?.factureCloture || 0);
  };

  // Update functions
  const updateBailStartDate = async () => {
    if (!bail?.id || !newStartDate) return;
    
    setUpdating(true);
    try {
      await apiService.updateBailStartDate(bail.id, newStartDate);
      // Utiliser onUpdate qui est maintenant partialRefetch
      onUpdate && onUpdate();
      setShowStartDateEdit(false);
    } catch (error) {
      console.error('Error updating start date:', error);
      console.error('Erreur lors de la mise à jour de la date:', error);
      // Pas d'alerte pop-up
    } finally {
      setUpdating(false);
    }
  };

  const updateBailEndDate = async () => {
    if (!bail?.id || !newEndDate) return;
    
    setUpdating(true);
    try {
      await apiService.updateBailEndDate(bail.id, newEndDate);
      // Utiliser onUpdate qui est maintenant partialRefetch
      onUpdate && onUpdate();
      setShowEndDateEdit(false);
    } catch (error) {
      console.error('Error updating end date:', error);
      console.error('Erreur lors de la mise à jour de la date:', error);
      // Pas d'alerte pop-up
    } finally {
      setUpdating(false);
    }
  };

  const updateFactureCloture = async () => {
    if (!bail?.id || newFactureCloture === '') return;
    
    setUpdating(true);
    try {
      await apiService.updateFactureCloture(bail.id, newFactureCloture);
      // Utiliser onUpdate qui est maintenant partialRefetch
      onUpdate && onUpdate();
      setShowFactureClotureEdit(false);
    } catch (error) {
      console.error('Error updating facture cloture:', error);
      console.error('Erreur lors de la mise à jour du montant:', error);
      // Pas d'alerte pop-up
    } finally {
      setUpdating(false);
    }
  };

  return {
    // State variables
    showStartDateEdit,
    showEndDateEdit,
    showFactureClotureEdit,
    newStartDate,
    newEndDate,
    newFactureCloture,
    updating,
    
    // State setters
    setNewStartDate,
    setNewEndDate,
    setNewFactureCloture,
    
    // Toggle functions
    toggleStartDateEdit,
    toggleEndDateEdit,
    toggleFactureClotureEdit,
    
    // Update functions
    updateBailStartDate,
    updateBailEndDate,
    updateFactureCloture
  };
};

export default useBailSummary;
